import { useQuery } from '@tanstack/react-query';
import { api } from '../../../../singleton/axios';
import dayjs from 'dayjs';

export interface PriceRecord15Min {
  date: string;
  open: number;
  low: number;
  high: number;
  close: number;
  volume: number;
}

export interface PriceDataResponse {
  data: PriceRecord15Min[];
  metadata: {
    requestedDate: string;
    actualDate: string;
    fallbackUsed: boolean;
    recordCount: number;
  };
}

export interface PriceDataWithMetadata {
  data: PriceRecord15Min[];
  requestedDate: string;
  actualDate: string;
  fallbackUsed: boolean;
  recordCount: number;
}

const getMostRecentTradingDay = (): string => {
  const today = dayjs();
  const dayOfWeek = today.day(); // 0 = Sunday, 6 = Saturday
  
  // If it's Saturday (6), go back to Friday
  if (dayOfWeek === 6) {
    return today.subtract(1, 'day').format('YYYY-MM-DD');
  }
  
  // If it's Sunday (0), go back to Friday
  if (dayOfWeek === 0) {
    return today.subtract(2, 'days').format('YYYY-MM-DD');
  }
  
  // Otherwise, use today (Monday-Friday)
  return today.format('YYYY-MM-DD');
};

/**
 * Hook for fetching 15-minute interval price data.
 * Supports both single-day and date range queries.
 *
 * @param ticker - Stock ticker symbol
 * @param fromDateOrTradingDate - Start date (YYYY-MM-DD) or single trading date
 * @param toDate - Optional end date (YYYY-MM-DD). If not provided, uses single-day mode.
 * @param enabled - Set false to keep the query mounted but never fetch
 */
export const useCompanyPrice15Min = (
  ticker: string | undefined,
  fromDateOrTradingDate?: string,
  toDate?: string,
  enabled: boolean = true
) => {
  // If toDate is provided, we're in date range mode
  // Otherwise, use single-day mode (backwards compatible)
  const effectiveFromDate = fromDateOrTradingDate ?? getMostRecentTradingDay();
  const effectiveToDate = toDate ?? effectiveFromDate;

  return useQuery<PriceDataWithMetadata, Error>({
    queryKey: ['companyPrice15Min', ticker, effectiveFromDate, effectiveToDate],
    queryFn: async () => {
      if (!ticker) {
        throw new Error('Ticker is required');
      }

      const params: any = {
        ticker,
        from: effectiveFromDate,
        to: effectiveToDate,
      };

      const { data: response } = await api.get<PriceDataResponse>('/company-price-15min', {
        params,
      });

      // Transform the response to a flatter structure for easier consumption
      return {
        data: response.data,
        requestedDate: response.metadata.requestedDate,
        actualDate: response.metadata.actualDate,
        fallbackUsed: response.metadata.fallbackUsed,
        recordCount: response.metadata.recordCount,
      };
    },
    enabled: enabled && !!ticker,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000, // 1 minute for 15-min data
  });
};